import { FC, useContext } from 'react';
import { ActionIcon, Box, Checkbox, Divider, Flex, Group, Select, Space, Stack, Tooltip } from '@mantine/core';
import { i18n } from '@lingui/core';
import {
  IconArrowDownCircle,
  IconArrowUpCircle,
  IconChevronDown,
  IconCopy,
  IconDragDrop,
  IconDragDrop2,
  IconTrashX,
} from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import { AttributeType, QuestionDto, QuestionHandlers } from '../../../Types/QuestionnaireT';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../../Service/TiptapS';
import { DataS } from '../../../Service/DataS';
import QuestionCustomAttribute from '../../../Organismns/Questionnaire/QuestionCustomAttribute';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import QuestionAnswers from '../../../Organismns/Questionnaire/QuestionAnswers';
import LinkText from '../../../Atoms/LinkText';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import { Language } from '../../../Types/DocumentT';
import QuestionActions from './QuestionActions';

interface Props {
  index: number;
  bundleId: string;
  questionDto: QuestionDto;
  questionHandlers: QuestionHandlers;
  questionsLength: number;
  setLoading: (val: boolean) => void;
  language: Language;
}

const Question: FC<Props> = ({
  index,
  bundleId,
  questionDto,
  questionHandlers,
  questionsLength,
  setLoading,
  language,
}) => {
  const {
    id: questionId,
    question,
    description,
    attributeType,
    printMinimalQuestion,
    printMinimalAnswer,
  } = questionDto;
  const {
    editQuestion,
    moveQuestion,
    insertQuestion,
    shiftQuestion,
    handleQuestionChange,
    duplicateQuestion,
    deleteQuestion,
  } = questionHandlers;
  const { lg } = useContext(WindowContext);
  const { isEventScope } = useEventScope();

  const answersAvailable = attributeType === 'SINGLE_CHOICE' || attributeType === 'MULTIPLE_CHOICE';
  const isFirst = index === 0;
  const isLast = index + 1 === questionsLength;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'question',
      item: { ...questionDto, bundleId },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [questionId],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'question',
      canDrop: (item: QuestionDto) => item.id !== questionId,
      collect: (monitor) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== questionId,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: any) => {
        setLoading(true);
        await moveQuestion(bundleId, item.id, questionId, item.bundleId === bundleId ? undefined : item.bundleId);
        setLoading(false);
      },
    }),
    [questionId],
  );

  const deleteHandler = () =>
    openDeleteConfirmModal({
      title: i18n._('questionnaire.question.delete.confirm.head'),
      description: i18n._('questionnaire.question.delete.confirm'),
      onConfirm: () => deleteQuestion(bundleId, questionId),
    });

  return (
    <div ref={drop} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Flex gap={5}>
        <div style={{ flexGrow: 1 }}>
          <Box
            p="sm"
            sx={(theme) => ({
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
              borderRadius: theme.radius.sm,
            })}
          >
            {!lg && (
              <Group position="right">
                <QuestionActions
                  questionHandlers={questionHandlers}
                  isFirst={isFirst}
                  isLast={isLast}
                  bundleId={bundleId}
                  questionId={questionId}
                  questionsLength={questionsLength}
                />
              </Group>
            )}
            <Flex gap="sm" direction={lg ? 'row' : 'column'}>
              <div style={{ flex: 1 }}>
                {isEventScope && (
                  <EventRichInput
                    label="Frage"
                    value={question ?? ''}
                    patchField={(htmlString) => editQuestion(questionId, { question: htmlString })}
                    menu={<DocumentBubbleMenu />}
                  />
                )}
                {!isEventScope && (
                  <RichInput
                    label="Frage"
                    value={question ?? ''}
                    patchField={(htmlString) => editQuestion(questionId, { question: htmlString })}
                    menu={<DocumentBubbleMenu />}
                  />
                )}
              </div>
              <Select
                label="Type"
                value={attributeType}
                onChange={(type: AttributeType) => editQuestion(questionId, { attributeType: type })}
                data={DataS.getAttributeTypesOptions(i18n)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={<IconChevronDown size={14} />}
                rightSectionWidth={25}
                sx={{ width: lg ? 180 : undefined }}
                pt={2}
              />
            </Flex>
            <Space h="sm" />
            {isEventScope && (
              <EventRichInput
                label="Ergänzende Angaben / Hilfestellung"
                value={description ?? ''}
                patchField={(htmlString) => editQuestion(questionId, { description: htmlString })}
                menu={<DocumentBubbleMenu />}
                styles={TiptapS.TextInputStyles}
              />
            )}
            {!isEventScope && (
              <RichInput
                label="Ergänzende Angaben / Hilfestellung"
                value={description ?? ''}
                patchField={(htmlString) => editQuestion(questionId, { description: htmlString })}
                menu={<DocumentBubbleMenu />}
                styles={TiptapS.TextInputStyles}
              />
            )}
            <Group position="right" ml="xs" mt={5}>
              <QuestionCustomAttribute
                question={questionDto}
                handleChange={(patch) => handleQuestionChange({ ...questionDto, ...patch })}
              />
              <Checkbox
                checked={printMinimalQuestion}
                onChange={() => editQuestion(questionId, { printMinimalQuestion: !printMinimalQuestion })}
                label="Frage minimal drucken"
                sx={{ paddingTop: 5 }}
              />
              {answersAvailable && (
                <Checkbox
                  checked={printMinimalAnswer}
                  onChange={() => editQuestion(questionId, { printMinimalAnswer: !printMinimalAnswer })}
                  label="Antwort minimal drucken"
                  sx={{ paddingTop: 5 }}
                />
              )}
            </Group>
            {answersAvailable && (
              <>
                <Divider mt="md" /> <QuestionAnswers question={questionDto} language={language} />{' '}
              </>
            )}
          </Box>
          <LinkText
            mt="sm"
            onClick={async () => {
              setLoading(true);
              await insertQuestion(bundleId, index + 1);
              setLoading(false);
            }}
          >
            + Frage hinzufügen
          </LinkText>
        </div>
        {lg && (
          <Stack justify="center">
            <Tooltip openDelay={300} position="left" transition="slide-left" label="Ziehen">
              <ActionIcon ref={drag} size="xs" style={{ cursor: 'grab' }}>
                {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 24 : 20} />}
                {!canDrop && <IconDragDrop2 style={{ cursor: 'grab' }} />}
              </ActionIcon>
            </Tooltip>
            {questionsLength > 1 && (
              <>
                {!isFirst && (
                  <Tooltip openDelay={300} position="left" transition="slide-left" label={i18n._('actions.up')}>
                    <ActionIcon size="xs">
                      <IconArrowUpCircle
                        onClick={async () => {
                          setLoading(true);
                          await shiftQuestion(bundleId, questionId, -1);
                          setLoading(false);
                        }}
                      />
                    </ActionIcon>
                  </Tooltip>
                )}
                {!isLast && (
                  <Tooltip openDelay={300} position="left" transition="slide-left" label={i18n._('actions.down')}>
                    <ActionIcon size="xs">
                      <IconArrowDownCircle
                        onClick={async () => {
                          setLoading(true);
                          await shiftQuestion(bundleId, questionId, 1);
                          setLoading(false);
                        }}
                      />
                    </ActionIcon>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip openDelay={300} position="left" transition="slide-left" label={i18n._('actions.duplicate')}>
              <ActionIcon size="xs">
                <IconCopy
                  onClick={async () => {
                    setLoading(true);
                    await duplicateQuestion(bundleId, questionId, index + 1);
                    setLoading(false);
                  }}
                />
              </ActionIcon>
            </Tooltip>
            <Tooltip openDelay={300} position="left" transition="slide-left" label={i18n._('actions.delete')}>
              <ActionIcon size="xs">
                <IconTrashX onClick={deleteHandler} />
              </ActionIcon>
            </Tooltip>
          </Stack>
        )}
      </Flex>
    </div>
  );
};

export default Question;
